import { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { useIsMobile } from '@/shared/hooks/export';
import { SearchResultItem } from '@/core/search/model/search';
import highlightMatchingString from '@/core/search/util/highlightMatchingString';
import { getSearchTypeLabel } from '@/core/search/util/get-search-type-label';
import SearchTypeIcon from '@/core/search/components/search-type-icon';
import { SearchContext, SearchFilterType } from '@/core/search/contexts/search-context';
import SearchActions from '@/core/search/components/search-actions';
import { CountryFlag, LoadMoreSentinel } from '@/shared/components/export';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown } from '@fortawesome/free-solid-svg-icons';

export default function SearchResultsList({
  type,
  results,
  expand,
  setExpandedType,
  linesToDisplay,
  filterType,
}: {
  type: SearchFilterType;
  results: SearchResultItem[];
  expand: boolean | null;
  setExpandedType: (type: SearchFilterType | null) => void;
  linesToDisplay: number;
  filterType?: SearchFilterType;
}) {
  const isMobile = useIsMobile(991);
  const { modalRef, previewed, search, setFilters, filters, hasMore, loadMore } =
    useContext(SearchContext)!;
  const [triggerDefaultAction, setTriggerDefaultAction] = useState<string | null>(null);

  useEffect(() => {
    if (document.querySelector('.modal')) {
      modalRef.current = document.querySelector('.modal') as HTMLDivElement;
    }
  }, [previewed, modalRef]);

  return (
    <div className="d-flex flex-column" style={{ maxHeight: '100%' }}>
      {(() => {
        const typeLabel = (
          <span className="d-flex align-items-center gap-2">
            <SearchTypeIcon type={type} />
            <span>{getSearchTypeLabel(type)}</span>
          </span>
        );

        const backLink = !filterType && (
          <span
            onClick={() => setExpandedType(expand ? null : type)}
            className="fw-bold text-secondary"
            role="button"
          >
            {expand ? 'Back to all results' : 'Show more'}
          </span>
        );

        const sortToggle = expand && type !== 'genre' && (
          <span
            onClick={() =>
              setFilters({ ...filters, sortBy: filters.sortBy ? undefined : 'date:desc' })
            }
            className={`d-flex${isMobile ? ' small' : ' fs-5 fw-bold'}`}
            role="button"
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--bs-tertiary-500)')}
            onMouseLeave={e => (e.currentTarget.style.color = '')}
          >
            <FontAwesomeIcon icon={faSortDown} className="me-2" />
            {filters.sortBy ? (
              <span>
                Sorted by date <span className="fst-italic">(click to sort by relevance)</span>
              </span>
            ) : (
              <span>
                Sorted by relevance <span className="fst-italic">(click to sort by date)</span>
              </span>
            )}
          </span>
        );

        return isMobile ? (
          <div className="p-2 pt-1 mb-2 border-bottom">
            <div className="d-flex align-items-center justify-content-between">
              {typeLabel}
              {backLink}
            </div>
            {sortToggle && <div className="mt-1">{sortToggle}</div>}
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-between p-2 pt-1 mb-2 border-bottom"
            style={{ position: 'sticky', top: 0, zIndex: 10 }}
          >
            {typeLabel}
            {sortToggle}
            {backLink}
          </div>
        );
      })()}
      <ul className="list-unstyled overflow-auto px-2" style={{ flexGrow: 1 }}>
        {results.slice(0, expand ? undefined : linesToDisplay).map(result => {
          const country = result.filters?.find(f => f.key === 'country')?.value;
          const rawTitle = type === 'event' ? `${result.title} [${country}]` : result.title;
          const isRecentResult =
            dayjs(result.date).isAfter(dayjs().subtract(6, 'hour')) &&
            dayjs(result.date).isBefore(dayjs().add(6, 'hour'));
          const isPdf = !!result.types?.includes('pdf');
          const titleNode = isPdf ? (
            <button
              type="button"
              aria-label={`PDF: ${rawTitle}`}
              className={`result-title${isMobile ? ' d-block' : ''}`}
              onClick={e => {
                e.stopPropagation();
                setTriggerDefaultAction(result.uri);
              }}
            >
              {highlightMatchingString(rawTitle, search)}
            </button>
          ) : (
            <span
              role="button"
              className={`result-title${isMobile ? ' d-block' : ''}`}
              onClick={async e => {
                e.stopPropagation();
                setTriggerDefaultAction(result.uri);
              }}
            >
              {!!country && <CountryFlag country={country} width={20} />}{' '}
              {highlightMatchingString(rawTitle, search)}
            </span>
          );
          return (
            <li
              key={result.uri}
              className={isMobile ? 'search-result-card' : 'd-flex justify-content-between'}
            >
              {titleNode}
              <div
                className={
                  isMobile
                    ? 'd-flex justify-content-between align-items-center mt-2'
                    : 'd-flex gap-4 align-items-center'
                }
              >
                <span
                  className={`small ${isRecentResult ? 'text-neutral-700' : 'text-neutral-500'} flex-shrink-0`}
                >
                  {type !== 'genre' ? dayjs(result.date).fromNow() : '-'}
                </span>
                <span
                  className="small text-neutral-500 flex-shrink-0"
                  style={isMobile ? undefined : { width: '80px' }}
                >
                  <SearchActions
                    item={result}
                    triggerDefaultAction={triggerDefaultAction}
                    setTriggerDefaultAction={setTriggerDefaultAction}
                  />
                </span>
              </div>
            </li>
          );
        })}
        {expand && hasMore && (
          <li>
            <LoadMoreSentinel key={results.length} onVisible={loadMore} />
          </li>
        )}
      </ul>
    </div>
  );
}
